import { useRef, useCallback, useState, useEffect } from 'react'
import { CheckCircle2, Lock, TrendingUp, Sparkles, Activity, ArrowUpRight, Flame, Shield, Zap } from 'lucide-react'

const STYLES = `
@keyframes hc-float1{0%,100%{transform:translateY(0) rotate(0)}25%{transform:translateY(-10px) rotate(0.8deg)}75%{transform:translateY(6px) rotate(-0.5deg)}}
@keyframes hc-float2{0%,100%{transform:translateY(0) rotate(0)}33%{transform:translateY(7px) rotate(-0.6deg)}66%{transform:translateY(-10px) rotate(0.6deg)}}
@keyframes hc-draw{0%{stroke-dashoffset:900}100%{stroke-dashoffset:0}}
@keyframes hc-area{0%{opacity:0;transform:translateY(6px)}100%{opacity:1;transform:translateY(0)}}
@keyframes hc-bob{0%,100%{transform:translateY(0)}50%{transform:translateY(-8px)}}
@keyframes hc-sonar{0%{transform:scale(1);opacity:.55}100%{transform:scale(2.8);opacity:0}}
@keyframes hc-sonar2{0%{transform:scale(1);opacity:.4}100%{transform:scale(2.2);opacity:0}}
@keyframes hc-shimmer{0%{left:-100%}100%{left:200%}}
@keyframes hc-neon{0%{background-position:0% 50%}100%{background-position:200% 50%}}
@keyframes hc-glow{0%,100%{opacity:.35}50%{opacity:.65}}
@keyframes hc-spin{0%{transform:rotate(0)}100%{transform:rotate(360deg)}}
@keyframes hc-ping{0%{transform:scale(1);opacity:.8}75%,100%{transform:scale(2.5);opacity:0}}
@keyframes hc-bar-grow{0%{transform:scaleY(0)}100%{transform:scaleY(1)}}
@keyframes hc-price-tick{0%,100%{opacity:1}50%{opacity:.5}}
@keyframes hc-line-glow{0%,100%{filter:drop-shadow(0 0 4px rgba(99,102,241,.4))}50%{filter:drop-shadow(0 0 12px rgba(99,102,241,.7))}}
@keyframes hc-gradient-shift{0%{background-position:0% 50%}50%{background-position:100% 50%}100%{background-position:0% 50%}}
@keyframes hc-badge-breathe{0%,100%{box-shadow:0 0 0 0 rgba(16,185,129,0.2)}50%{box-shadow:0 0 0 8px rgba(16,185,129,0)}}
@keyframes hc-number-glow{0%,100%{text-shadow:0 0 0 transparent}50%{text-shadow:0 0 30px rgba(99,102,241,0.15)}}
@keyframes hc-resistance{0%,100%{opacity:.3}50%{opacity:.7}}
`

const VOLUME_BARS = [35, 50, 30, 65, 45, 80, 55, 90, 40, 70, 85, 60, 75, 95, 50, 65, 45, 80, 70, 55, 85, 60, 90, 75]

interface HeroCardProps { symbol: string }

export function HeroCard({ symbol }: HeroCardProps) {
    const cardRef = useRef<HTMLDivElement>(null)
    const [tilt, setTilt] = useState({ rX: 0, rY: 0, sX: 50, sY: 50 })
    const [hovering, setHovering] = useState(false)

    useEffect(() => {
        if (document.getElementById('hc-styles')) return
        const s = document.createElement('style')
        s.id = 'hc-styles'
        s.textContent = STYLES
        document.head.appendChild(s)
        return () => { s.remove() }
    }, [])

    const onMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
        const el = cardRef.current
        if (!el) return
        const r = el.getBoundingClientRect()
        const x = e.clientX - r.left, y = e.clientY - r.top
        setTilt({
            rX: ((y - r.height / 2) / (r.height / 2)) * -7,
            rY: ((x - r.width / 2) / (r.width / 2)) * 7,
            sX: (x / r.width) * 100,
            sY: (y / r.height) * 100,
        })
    }, [])

    return (
        <div className="relative mt-10 lg:mt-0 max-w-[580px] mx-auto w-full" style={{ perspective: '1400px' }}>
            {/* Ambient halos */}
            <div className="absolute -inset-12 pointer-events-none" style={{ background: 'radial-gradient(ellipse at 35% 30%, rgba(99,102,241,0.2), transparent 60%)', filter: 'blur(50px)', animation: 'hc-glow 5s ease-in-out infinite' }} />
            <div className="absolute -inset-12 pointer-events-none" style={{ background: 'radial-gradient(ellipse at 70% 70%, rgba(168,85,247,0.15), transparent 55%)', filter: 'blur(60px)', animation: 'hc-glow 5s ease-in-out infinite 2s' }} />

            {/* ════ BADGE: Gamma Blast ════ */}
            <div className="absolute z-40 -right-2 md:-right-5 top-3 md:top-6" style={{ animation: 'hc-float1 5s ease-in-out infinite' }}>
                <div className="relative p-3.5 rounded-2xl overflow-hidden" style={{ background: 'rgba(255,255,255,0.82)', backdropFilter: 'blur(24px)', boxShadow: '0 16px 40px -8px rgba(244,63,94,0.2), inset 0 0 0 1px rgba(255,255,255,0.6)' }}>
                    <div className="hidden dark:block absolute inset-0 rounded-2xl" style={{ background: 'rgba(15,23,42,0.82)' }} />
                    <div className="relative z-10 flex items-center gap-2.5 mb-2">
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white" style={{ background: 'linear-gradient(135deg, #f43f5e, #e11d48)', boxShadow: '0 4px 14px -2px rgba(244,63,94,0.5)' }}>
                            <Flame size={14} strokeWidth={3} />
                        </div>
                        <div>
                            <div className="text-[13px] font-extrabold text-slate-800 dark:text-white tracking-wide">Gamma Blast</div>
                            <div className="text-[9px] font-bold text-rose-500 tracking-wider">IV RANK 85%</div>
                        </div>
                    </div>
                    <div className="relative z-10 w-32 h-2 rounded-full overflow-hidden" style={{ background: 'rgba(0,0,0,0.06)' }}>
                        <div className="h-full rounded-full" style={{ width: '85%', background: 'linear-gradient(90deg, #f43f5e, #ec4899, #f43f5e)', backgroundSize: '200% 100%', animation: 'hc-neon 1.8s linear infinite', boxShadow: '0 0 8px rgba(244,63,94,0.5)' }} />
                    </div>
                </div>
            </div>

            {/* ════ MAIN CARD ════ */}
            <div
                ref={cardRef}
                className="relative rounded-[28px] overflow-hidden cursor-default"
                style={{
                    transform: `rotateX(${tilt.rX}deg) rotateY(${tilt.rY}deg) scale(${hovering ? 1.02 : 1})`,
                    transition: 'transform 0.4s cubic-bezier(0.22,1,0.36,1)',
                    transformStyle: 'preserve-3d',
                    willChange: 'transform',
                }}
                onMouseMove={onMove}
                onMouseEnter={() => setHovering(true)}
                onMouseLeave={() => { setHovering(false); setTilt({ rX: 0, rY: 0, sX: 50, sY: 50 }) }}
            >
                {/* Animated gradient border */}
                <div className="absolute -inset-px rounded-[28px] overflow-hidden z-0">
                    <div className="absolute -inset-[100%]" style={{
                        background: 'conic-gradient(from 180deg, #6366f1, #a855f7, #ec4899, #f43f5e, #f97316, #eab308, #10b981, #6366f1)',
                        animation: hovering ? 'hc-spin 4s linear infinite' : 'none',
                        opacity: hovering ? 0.6 : 0.12,
                        transition: 'opacity 0.6s ease',
                    }} />
                </div>

                {/* Card body */}
                <div className="relative z-10 rounded-[28px] p-1" style={{ background: 'transparent' }}>
                    <div className="rounded-[24px] p-5 md:p-7 relative overflow-hidden" style={{ background: 'linear-gradient(170deg, rgba(255,255,255,0.97) 0%, rgba(248,250,252,0.95) 50%, rgba(241,245,249,0.93) 100%)', backdropFilter: 'blur(40px)' }}>
                        <div className="hidden dark:block absolute inset-0 rounded-[24px]" style={{ background: 'linear-gradient(170deg, rgba(15,23,42,0.97) 0%, rgba(10,15,30,0.95) 100%)' }} />

                        {/* Spotlight */}
                        <div className="absolute inset-0 rounded-[24px] pointer-events-none z-30 transition-opacity duration-300" style={{
                            background: hovering ? `radial-gradient(450px circle at ${tilt.sX}% ${tilt.sY}%, rgba(99,102,241,0.08), rgba(168,85,247,0.04) 35%, transparent 60%)` : 'transparent',
                            opacity: hovering ? 1 : 0,
                        }} />

                        {/* Shimmer */}
                        <div className="absolute inset-0 rounded-[24px] overflow-hidden pointer-events-none z-30">
                            <div className="absolute top-0 w-[35%] h-full" style={{
                                background: 'linear-gradient(105deg, transparent, rgba(255,255,255,0.04) 40%, rgba(255,255,255,0.1) 50%, rgba(255,255,255,0.04) 60%, transparent)',
                                animation: 'hc-shimmer 6s ease-in-out infinite',
                            }} />
                        </div>

                        {/* ── HEADER ── */}
                        <div className="relative z-10 flex items-center justify-between mb-5 pb-4" style={{ borderBottom: '1px solid rgba(226,232,240,0.4)' }}>
                            <div className="flex items-center gap-3.5">
                                {/* Clean TrendingUp Icon */}
                                <div className="w-[48px] h-[48px] rounded-[13px] flex items-center justify-center bg-[#005451] shadow-md">
                                    <TrendingUp size={24} className="text-white" strokeWidth={2.5} />
                                </div>
                                {/* Price */}
                                <div>
                                    <div className="text-[9px] font-bold tracking-[0.2em] text-slate-400 dark:text-slate-500 uppercase mb-0.5">{symbol}</div>
                                    <div className="flex items-baseline gap-1.5">
                                        <span className="text-[1.5rem] font-black text-slate-900 dark:text-white font-mono tracking-tight leading-none" style={{ animation: 'hc-number-glow 4s ease-in-out infinite' }}>24,850</span>
                                        <span className="text-sm font-bold text-slate-300 dark:text-slate-600 font-mono">.45</span>
                                        <span className="ml-1 text-[10px] font-bold font-mono px-1.5 py-px rounded-md flex items-center gap-0.5" style={{ color: '#10b981', background: 'rgba(16,185,129,0.07)', border: '1px solid rgba(16,185,129,0.12)' }}>
                                            <ArrowUpRight size={9} strokeWidth={3} />+0.85%
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                {/* Mini sparkline */}
                                <svg width="48" height="20" viewBox="0 0 48 20" className="hidden sm:block">
                                    <path d="M0,14 C4,13 8,15 12,12 C16,9 20,6 24,8 C28,10 32,5 36,4 C40,3 44,6 48,4" fill="none" stroke="#10b981" strokeWidth="1.5" strokeLinecap="round" />
                                    <path d="M0,14 C4,13 8,15 12,12 C16,9 20,6 24,8 C28,10 32,5 36,4 C40,3 44,6 48,4 V20 H0 Z" fill="url(#miniGrad)" />
                                    <defs><linearGradient id="miniGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#10b981" stopOpacity=".15" /><stop offset="100%" stopColor="#10b981" stopOpacity="0" /></linearGradient></defs>
                                </svg>
                                {/* Live badge */}
                                <div className="px-2.5 py-1 rounded-full text-[9px] font-bold flex items-center gap-1.5" style={{ color: '#10b981', background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.1)' }}>
                                    <span className="relative flex h-1.5 w-1.5">
                                        <span className="absolute inline-flex h-full w-full rounded-full" style={{ background: '#10b981', animation: 'hc-ping 1.5s cubic-bezier(0,0,0.2,1) infinite' }} />
                                        <span className="relative inline-flex rounded-full h-1.5 w-1.5" style={{ background: '#10b981', boxShadow: '0 0 4px rgba(16,185,129,.7)' }} />
                                    </span>
                                    LIVE
                                </div>
                            </div>
                        </div>

                        {/* ── CHART ── */}
                        <div className="relative z-10 h-44 md:h-48 w-full rounded-xl mb-5 overflow-hidden" style={{ background: 'linear-gradient(180deg, rgba(248,250,252,0.5), rgba(255,255,255,0.2))', border: '1px solid rgba(226,232,240,0.35)', boxShadow: 'inset 0 1px 6px rgba(0,0,0,0.015)' }}>
                            <div className="hidden dark:block absolute inset-0 rounded-xl" style={{ background: 'rgba(255,255,255,0.015)', border: '1px solid rgba(255,255,255,0.04)' }} />

                            {/* Grid + labels */}
                            {[25, 50, 75].map(p => <div key={p} className="absolute inset-x-3 h-px" style={{ top: `${p}%`, background: 'rgba(148,163,184,0.08)' }} />)}
                            <div className="absolute left-1.5 top-[22%] text-[7px] font-mono font-bold text-slate-300/60 dark:text-slate-600/60">25,100</div>
                            <div className="absolute left-1.5 top-[47%] text-[7px] font-mono font-bold text-slate-300/60 dark:text-slate-600/60">24,900</div>
                            <div className="absolute left-1.5 top-[72%] text-[7px] font-mono font-bold text-slate-300/60 dark:text-slate-600/60">24,700</div>

                            {/* Support / Resistance zones */}
                            <div className="absolute inset-x-0 h-[18px] rounded-sm" style={{ top: '18%', background: 'linear-gradient(180deg, rgba(244,63,94,0.04), rgba(244,63,94,0.01))', borderTop: '1px dashed rgba(244,63,94,0.15)', animation: 'hc-resistance 3s ease-in-out infinite' }} />
                            <div className="absolute right-2 text-[7px] font-mono font-bold" style={{ top: '16%', color: 'rgba(244,63,94,0.4)' }}>R1</div>
                            <div className="absolute inset-x-0 h-[18px] rounded-sm" style={{ bottom: '22%', background: 'linear-gradient(0deg, rgba(16,185,129,0.04), rgba(16,185,129,0.01))', borderBottom: '1px dashed rgba(16,185,129,0.15)', animation: 'hc-resistance 3s ease-in-out infinite 1.5s' }} />
                            <div className="absolute right-2 text-[7px] font-mono font-bold" style={{ bottom: '20%', color: 'rgba(16,185,129,0.4)' }}>S1</div>

                            <svg className="absolute inset-0 w-full h-full" preserveAspectRatio="none" viewBox="0 0 600 200">
                                <defs>
                                    <linearGradient id="hcA" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor="#6366f1" stopOpacity=".28" />
                                        <stop offset="45%" stopColor="#8b5cf6" stopOpacity=".08" />
                                        <stop offset="100%" stopColor="#6366f1" stopOpacity="0" />
                                    </linearGradient>
                                    <linearGradient id="hcL" x1="0" y1="0" x2="1" y2="0">
                                        <stop offset="0%" stopColor="#818cf8" /><stop offset="30%" stopColor="#6366f1" />
                                        <stop offset="60%" stopColor="#7c3aed" /><stop offset="100%" stopColor="#6366f1" />
                                    </linearGradient>
                                    <filter id="hcG"><feGaussianBlur stdDeviation="3.5" result="b" /><feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge></filter>
                                </defs>
                                {/* Area */}
                                <path d="M0,140 C60,138 100,142 180,135 C260,125 310,75 400,55 C460,42 530,52 600,46 V200H0Z" fill="url(#hcA)" style={{ animation: 'hc-area 1.2s ease-out 2s both' }} />
                                {/* Line */}
                                <path d="M0,140 C60,138 100,142 180,135 C260,125 310,75 400,55 C460,42 530,52 600,46" fill="none" stroke="url(#hcL)" strokeWidth="2.5" strokeLinecap="round" filter="url(#hcG)" style={{ strokeDasharray: 900, strokeDashoffset: 900, animation: 'hc-draw 2.2s cubic-bezier(0.4,0,0.2,1) 0.4s forwards, hc-line-glow 3s ease-in-out 3s infinite' }} />
                            </svg>

                            {/* Volume bars */}
                            <div className="absolute bottom-5 inset-x-3 h-[22px] flex items-end gap-[2px]">
                                {VOLUME_BARS.map((h, i) => (
                                    <div key={i} className="flex-1 rounded-t-[1px]" style={{
                                        height: `${h}%`,
                                        background: h > 75 ? 'rgba(99,102,241,0.2)' : 'rgba(148,163,184,0.1)',
                                        transformOrigin: 'bottom',
                                        animation: `hc-bar-grow 0.5s ease-out ${0.1 + i * 0.03}s both`,
                                    }} />
                                ))}
                            </div>

                            {/* Tooltip */}
                            <div className="absolute z-20" style={{ top: '18%', right: '16%', animation: 'hc-bob 4.5s ease-in-out infinite' }}>
                                <div className="flex items-center gap-2">
                                    <div className="relative flex items-center justify-center">
                                        <div className="absolute w-7 h-7 rounded-full" style={{ background: 'rgba(99,102,241,0.12)', animation: 'hc-sonar 2s cubic-bezier(0,0,0.2,1) infinite' }} />
                                        <div className="absolute w-5 h-5 rounded-full" style={{ background: 'rgba(99,102,241,0.18)', animation: 'hc-sonar 2s cubic-bezier(0,0,0.2,1) infinite 0.5s' }} />
                                        <div className="w-3 h-3 rounded-full border-2 border-white dark:border-slate-900" style={{ background: 'linear-gradient(135deg, #6366f1, #7c3aed)', boxShadow: '0 0 14px rgba(99,102,241,.7)' }} />
                                    </div>
                                    <div className="px-3 py-1.5 rounded-lg text-[12px] font-extrabold text-slate-800 dark:text-white flex items-center gap-1.5" style={{ background: 'rgba(255,255,255,0.93)', backdropFilter: 'blur(16px)', boxShadow: '0 8px 30px -6px rgba(99,102,241,0.18), inset 0 0 0 1px rgba(0,0,0,0.03)' }}>
                                        <div className="hidden dark:block absolute inset-0 rounded-lg" style={{ background: 'rgba(30,41,59,0.93)' }} />
                                        <Sparkles size={11} className="text-violet-500 relative z-10" />
                                        <span className="relative z-10">High Prob Sell</span>
                                    </div>
                                </div>
                            </div>

                            {/* Secondary dot */}
                            <div className="absolute z-20" style={{ top: '62%', left: '22%' }}>
                                <div className="relative flex items-center justify-center">
                                    <div className="absolute w-4 h-4 rounded-full" style={{ background: 'rgba(16,185,129,0.12)', animation: 'hc-sonar2 3s ease-out infinite' }} />
                                    <div className="w-2 h-2 rounded-full border-[1.5px] border-white dark:border-slate-900" style={{ background: '#10b981', boxShadow: '0 0 6px rgba(16,185,129,.4)' }} />
                                </div>
                            </div>

                            {/* Time axis */}
                            <div className="absolute bottom-1 inset-x-3 flex justify-between">
                                {['9:15', '10:30', '11:45', '13:00', '14:15', '15:30'].map(t => (
                                    <span key={t} className="text-[6px] font-mono font-bold text-slate-300/50 dark:text-slate-600/50">{t}</span>
                                ))}
                            </div>
                        </div>

                        {/* ── STATS GRID ── */}
                        <div className="relative z-10 grid grid-cols-3 gap-2.5">
                            {/* PCR */}
                            <StatCard
                                icon={<Activity size={10} strokeWidth={2.5} />}
                                label="PCR"
                                value="0.85"
                                tag={{ text: 'Bullish', color: '#10b981' }}
                                sparkData={[14, 12, 15, 10, 8, 11, 9, 7, 8, 6]}
                                sparkColor="#10b981"
                            />
                            {/* Max Pain */}
                            <StatCard
                                icon={<Shield size={10} strokeWidth={2.5} />}
                                label="Max Pain"
                                value="24,800"
                                tag={{ text: 'Held', color: '#6366f1' }}
                                sparkData={[8, 9, 8, 10, 9, 10, 10, 9, 10, 10]}
                                sparkColor="#6366f1"
                            />
                            {/* Shift */}
                            <StatCard
                                icon={<Zap size={10} strokeWidth={2.5} />}
                                label="OI Shift"
                                value="+2.4%"
                                tag={{ text: 'CE Heavy', color: '#f43f5e' }}
                                sparkData={[5, 7, 6, 9, 11, 10, 13, 12, 14, 16]}
                                sparkColor="#f43f5e"
                            />
                        </div>

                        {/* ── BOTTOM TICKER STRIP ── */}
                        <div className="relative z-10 mt-4 flex items-center gap-2 overflow-hidden rounded-lg px-3 py-2" style={{ background: 'rgba(99,102,241,0.03)', border: '1px solid rgba(99,102,241,0.06)' }}>
                            <div className="hidden dark:block absolute inset-0 rounded-lg" style={{ background: 'rgba(99,102,241,0.04)', border: '1px solid rgba(99,102,241,0.08)' }} />
                            <Zap size={10} className="text-[#005451] relative z-10 shrink-0" strokeWidth={3} />
                            <div className="relative z-10 flex items-center gap-3 text-[10px] font-mono font-bold text-slate-500 dark:text-slate-400 whitespace-nowrap overflow-hidden">
                                <span>CE OI: <span className="text-emerald-500">+1.2L</span></span>
                                <span className="text-slate-300 dark:text-slate-700">|</span>
                                <span>PE OI: <span className="text-rose-500">-0.8L</span></span>
                                <span className="text-slate-300 dark:text-slate-700">|</span>
                                <span>Vol: <span className="text-[#005451]">2.4M</span></span>
                                <span className="text-slate-300 dark:text-slate-700">|</span>
                                <span>IV: <span className="text-amber-500">18.2%</span></span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* ════ BADGE: Trade Executed ════ */}
            <div className="absolute z-40 -left-2 md:-left-4 -bottom-2 md:-bottom-5" style={{ animation: 'hc-float2 6s ease-in-out infinite' }}>
                <div className="relative p-3.5 md:p-4 rounded-2xl overflow-hidden" style={{ background: 'rgba(255,255,255,0.85)', backdropFilter: 'blur(24px)', boxShadow: '0 16px 40px -8px rgba(16,185,129,0.18), inset 0 0 0 1px rgba(255,255,255,0.5)', animation: 'hc-badge-breathe 3s ease-in-out infinite' }}>
                    <div className="hidden dark:block absolute inset-0 rounded-2xl" style={{ background: 'rgba(15,23,42,0.85)' }} />
                    <div className="relative z-10 flex items-center gap-3">
                        <div className="relative">
                            <div className="absolute inset-[-4px] rounded-full" style={{ animation: 'hc-sonar2 2.5s ease-out infinite' }} />
                            <div className="w-10 h-10 rounded-full flex items-center justify-center text-white relative" style={{ background: 'linear-gradient(145deg, #34d399, #059669)', boxShadow: '0 4px 16px -3px rgba(16,185,129,0.45)' }}>
                                <div className="absolute inset-0 rounded-full" style={{ border: '2px solid rgba(255,255,255,0.2)', borderTopColor: 'transparent', borderRightColor: 'transparent', animation: 'hc-spin 3s linear infinite' }} />
                                <CheckCircle2 size={18} strokeWidth={3} className="relative z-10" />
                            </div>
                        </div>
                        <div>
                            <div className="text-[13px] font-extrabold text-slate-900 dark:text-white tracking-wide">Trade Executed</div>
                            <div className="text-[10px] font-mono font-bold tracking-wider" style={{ color: '#10b981' }}>
                                #8B2-99A <span style={{ opacity: 0.5 }}>• 0.34s</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

/* ── Stat Card Sub-component ── */
function StatCard({ icon, label, value, tag, sparkData, sparkColor }: {
    icon: React.ReactNode; label: string; value: string
    tag: { text: string; color: string }
    sparkData: number[]; sparkColor: string
}) {
    const max = Math.max(...sparkData)
    const points = sparkData.map((v, i) => `${(i / (sparkData.length - 1)) * 44},${18 - (v / max) * 14}`).join(' ')

    return (
        <div className="group relative p-3.5 rounded-xl overflow-hidden transition-all duration-300 hover:scale-[1.03]" style={{ background: 'rgba(255,255,255,0.45)', border: '1px solid rgba(226,232,240,0.4)' }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = tag.color + '30'; e.currentTarget.style.boxShadow = `0 6px 24px -6px ${tag.color}15` }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(226,232,240,0.4)'; e.currentTarget.style.boxShadow = 'none' }}
        >
            <div className="hidden dark:block absolute inset-0 rounded-xl" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.04)' }} />
            <div className="relative z-10">
                <div className="flex items-center justify-between mb-1.5">
                    <div className="flex items-center gap-1">
                        <span className="text-slate-400">{icon}</span>
                        <span className="text-[8px] font-bold uppercase tracking-[0.12em] text-slate-400 dark:text-slate-500">{label}</span>
                    </div>
                    {/* Mini sparkline */}
                    <svg width="44" height="18" viewBox="0 0 44 18" className="opacity-50 group-hover:opacity-100 transition-opacity">
                        <polyline points={points} fill="none" stroke={sparkColor} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </div>
                <div className="text-[1.35rem] font-black text-slate-900 dark:text-white font-mono tracking-tighter leading-none mb-1.5" style={{ animation: 'hc-number-glow 4s ease-in-out infinite' }}>
                    {value}
                </div>
                <div className="inline-flex items-center gap-0.5 text-[9px] font-bold px-1.5 py-px rounded-md" style={{ color: tag.color, background: tag.color + '0a', border: `1px solid ${tag.color}15` }}>
                    {tag.text}
                </div>
            </div>
        </div>
    )
}
